import { useEffect } from 'react';
import { Box, Button, Card, CardContent, Container, Divider, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../components/branding/BrandLogo';
import { LEGAL_NOTICE } from '../legalNotice';
import { CURRENT_PRIVACY_VERSION, CURRENT_TERMS_VERSION } from '../legalVersions';

const LEGAL_UPDATED_AT = 'June 19, 2026';
const LEGAL_UPDATED_AT_ISO = '2026-06-19';
const LEGAL_TODO_FR = 'À compléter avant l’ouverture publique';

const legalPages = {
  '/privacy': {
    eyebrow: 'Privacy',
    title: 'Privacy Policy',
    subtitle:
      'How The Tavern collects, uses, protects, and lets you control your personal data.',
    meta: `Version: ${CURRENT_PRIVACY_VERSION} - Last updated: ${LEGAL_UPDATED_AT_ISO}`,
    sections: [
      {
        id: 'data-controller',
        title: '1. Data Controller',
        body: [
          'The Tavern is the data controller for the personal data processed through the Tavern web application available at thetavern.fr.',
          'The Tavern is operated by Sarto for the purpose of providing a board game library and game night planning service.',
        ],
      },
      {
        id: 'contact-information',
        title: '2. Contact Information',
        body: [
          'For privacy questions or GDPR requests, contact contact@thetavern.fr.',
          'You can use this address for access, correction, deletion, portability, objection, or restriction requests.',
        ],
        links: [
          { label: 'Manage privacy requests', to: '/privacy/contact' },
        ],
      },
      {
        id: 'data-we-collect',
        title: '3. Data We Collect',
        body: [
          'Account data: username, email address, password hash, email verification timestamp, account creation timestamp, password reset token metadata, and email verification token metadata.',
          'Profile data: display name, avatar URL or uploaded avatar data, tavern title, motto, favorite game identifiers, profile tags, seals, profile statistics, and update timestamps.',
          'Social and presence data: companion relationships, invitation or friendship status, online presence, last seen timestamp, and related timestamps.',
          'Game library data: games you add, BoardGameGeek identifiers and imported metadata, localized game metadata, ratings, ownership status, notes, and timestamps.',
          'Game night data: hosted nights, invited companions, location name and optional location details, candidate games, assignments, votes, notifications, and related timestamps.',
          'Local device data: authentication token, language preference, game night drafts, PWA install prompt state, and cached header profile data stored in browser storage.',
          'The current database does not include a separate groups table. If group features are added, group membership and related activity will be documented here.',
        ],
      },
      {
        id: 'why-we-collect-data',
        title: '4. Why We Collect Data',
        body: [
          'We process data to create and secure accounts, authenticate users, verify email addresses, let users manage game libraries, invite companions, plan game nights, vote for games, send transactional notices, display notifications, maintain presence information, and keep the service reliable.',
          'We also process limited technical data for security, abuse prevention, debugging, service operation, legal compliance, future support requests, and email notification preferences.',
          'We do not sell your personal data.',
        ],
      },
      {
        id: 'legal-basis',
        title: '5. Legal Basis',
        body: [
          'Contract: data needed to create your account, authenticate you, provide your shelf, companions, game nights, votes, and notifications.',
          'Legitimate interests: service security, fraud prevention, abuse prevention, debugging, reliability, and product improvement that does not override user rights.',
          'Consent: optional features that require consent, such as future analytics, marketing emails, or non-essential cookies, will be activated only when the relevant consent exists.',
          'Legal obligations: data may be retained or processed when required to comply with applicable law or respond to valid legal requests.',
        ],
      },
      {
        id: 'data-retention',
        title: '6. Data Retention',
        body: [
          'Data is retained only for as long as necessary to provide the service, comply with legal obligations, resolve disputes, and enforce agreements.',
          'The source of truth for retention rules is the repository document docs/data-retention-policy.md.',
          'Active account data is kept while the account exists. When an account is deleted, personal account data, active tokens, and account-related records are deleted immediately unless a limited technical or legal retention is strictly required.',
          'Technical logs, transactional email metadata, and security events may be retained for up to 12 months. Notifications may be retained for up to 24 months. Privacy request records, if stored in the future, may be retained for 24 months as compliance evidence.',
          'Expired invitations should be purged automatically once expiration-based cleanup jobs exist. Legal consent records and email preferences are kept while the account remains active and are removed with the account.',
          'The Tavern may add an inactive account lifecycle later: for example, accounts inactive for more than 36 months could receive a warning before deletion or anonymization after an additional grace period.',
        ],
      },
      {
        id: 'third-party-providers',
        title: '7. Third-Party Providers',
        body: [
          'Hosting: the production application runs on a server operated for The Tavern. Detailed hosting provider information will be completed before the public beta opens.',
          'Email: transactional emails are prepared by the application and may be sent through OVH SMTP using noreply@thetavern.fr. The codebase also supports Resend as an optional provider if configured later.',
          'BoardGameGeek: when you import or search board games, The Tavern may call BoardGameGeek APIs and store imported game metadata in your library.',
          'Cloudflare Turnstile: public registration may use Turnstile to protect signup from abuse.',
          'No analytics, monitoring, marketing, or advertising provider is currently documented as active in the repository. If these are added later, this list, email preferences, and the consent flow will be updated.',
        ],
      },
      {
        id: 'cookies',
        title: '8. Cookies',
        body: [
          'The Tavern uses essential browser storage for authentication, security, language preference, draft game night data, PWA install prompt state, cached header profile data, and cookie consent preferences.',
          'Essential storage is always enabled because it is needed for the service to work.',
          'Optional categories are analytics, performance, and marketing. No analytics, monitoring, marketing, or advertising provider is currently active in the application.',
          'If optional providers such as Google Analytics, PostHog, Mixpanel, Facebook Pixel, Hotjar, or Microsoft Clarity are added later, they must be mapped to a consent category and blocked until the relevant consent is granted.',
        ],
        links: [
          { label: 'Cookie preferences', to: '/settings' },
        ],
      },
      {
        id: 'user-rights',
        title: '9. User Rights',
        body: [
          'Under GDPR, you may have the right of access, right to rectification, right to erasure, right to restriction of processing, right to object, and right to data portability.',
          'You may also have the right to withdraw consent where processing is based on consent.',
          'These rights may be subject to legal limits, identity verification, or technical constraints needed to protect other users and the integrity of the service.',
        ],
      },
      {
        id: 'account-deletion',
        title: '10. Account Deletion',
        body: [
          'The self-service account deletion feature is available from Settings > Privacy & Data.',
          'Users can also request help with account deletion by contacting contact@thetavern.fr.',
          'Deletion requests remove account data and associated personal data immediately unless limited retention is required for legal, security, or operational reasons.',
        ],
      },
      {
        id: 'email-preferences',
        title: '11. Email Preferences',
        body: [
          'Authenticated users can manage optional email preferences from the Notifications section on their profile page.',
          'Event invitations, group activity, and product updates are enabled by default and can be disabled later.',
          'Marketing emails are disabled by default and are sent only after an explicit opt-in.',
          'Transactional emails such as account verification, password reset, security alerts, legal notices, and required compliance messages are not controlled by optional email preferences.',
        ],
        links: [
          { label: 'Manage email preferences', to: '/settings' },
        ],
      },
      {
        id: 'exercising-your-rights',
        title: '12. Exercising Your Rights',
        body: [
          'Send privacy requests to contact@thetavern.fr.',
          'Examples include access requests, deletion requests, correction requests, portability requests, restriction requests, and objections to processing.',
          'To protect accounts, The Tavern may ask for information needed to verify your identity before processing a request.',
        ],
        links: [
          { label: 'Manage privacy requests', to: '/privacy/contact' },
        ],
      },
      {
        id: 'policy-updates',
        title: '13. Policy Updates',
        body: [
          `Current policy version: ${CURRENT_PRIVACY_VERSION}. Last updated: ${LEGAL_UPDATED_AT_ISO}.`,
          'This policy may be updated as The Tavern evolves, as new providers are added, or as legal requirements change.',
          'When material changes require a new acceptance, The Tavern may ask users to review and accept the updated version.',
        ],
      },
    ],
  },
  '/terms': {
    eyebrow: 'Terms',
    title: 'Terms of Service',
    subtitle: 'The rules for using The Tavern.',
    meta: `Version: ${CURRENT_TERMS_VERSION} - Last updated: ${LEGAL_UPDATED_AT_ISO}`,
    sections: [
      {
        id: 'purpose-of-the-service',
        title: '1. Purpose of the Service',
        body: [
          'The Tavern helps users create and manage board game libraries, connect with companions, organize game nights, send invitations, vote for games, and receive related notifications.',
          'The service is an evolving product. Features may change, improve, or be removed as The Tavern develops.',
        ],
      },
      {
        id: 'account-creation',
        title: '2. Account Creation',
        body: [
          'To use the service, you may need to create an account with a username, email address, and password.',
          'You must provide accurate information, keep your account credentials confidential, and notify us if you believe your account has been compromised.',
          'You are responsible for activity that happens through your account unless the activity results from a security issue caused by The Tavern.',
        ],
      },
      {
        id: 'user-responsibilities',
        title: '3. User Responsibilities',
        body: [
          'You agree to use The Tavern lawfully and respectfully.',
          'You are responsible for the accuracy and legality of the information you add, including profile details, game library entries, game notes, game night details, and invitations.',
          'You must respect other users and avoid behavior that could harm, harass, mislead, or disrupt the community.',
        ],
      },
      {
        id: 'acceptable-use',
        title: '4. Acceptable Use',
        body: [
          'You must not use The Tavern to break the law, impersonate another person, send spam, scrape private data, bypass access controls, disrupt the service, or upload malicious content.',
          'You must not attempt to access data or accounts that you are not authorized to access.',
        ],
      },
      {
        id: 'user-content',
        title: '5. User Content',
        body: [
          'You keep responsibility for content you provide to The Tavern, including profile text, avatars, library notes, invitations, and game night information.',
          'By adding content, you allow The Tavern to process, display, store, and transmit it as needed to provide the service.',
          'Do not add content that is unlawful, infringing, abusive, or harmful.',
        ],
      },
      {
        id: 'groups-game-nights-and-invitations',
        title: '6. Groups, Game Nights and Invitations',
        body: [
          'The Tavern lets users organize game nights, invite companions, vote for games, and coordinate who brings what.',
          'You are responsible for the invitations, locations, dates, and details you share with other users.',
          'If group features are added, group membership and related rules will follow the same principles: use the service lawfully, respectfully, and only for intended collaboration.',
        ],
      },
      {
        id: 'notifications-and-emails',
        title: '7. Notifications and Emails',
        body: [
          'The Tavern may send transactional emails and in-app notifications for account verification, password reset, invitations, game night updates, and service security.',
          'Marketing emails, if introduced later, will require an appropriate opt-in and unsubscribe mechanism.',
        ],
      },
      {
        id: 'intellectual-property',
        title: '8. Intellectual Property',
        body: [
          'The Tavern interface, branding, text, and original assets belong to their respective owners.',
          'Board game names, covers, metadata, publishers, and designer information may belong to third parties. BoardGameGeek metadata remains subject to BoardGameGeek and rightsholder terms.',
          'You must not copy, modify, or reuse The Tavern materials outside the service without permission unless the law allows it.',
        ],
      },
      {
        id: 'service-availability',
        title: '9. Service Availability',
        body: [
          'The Tavern is provided as an evolving service without a guarantee of uninterrupted or error-free availability.',
          'Maintenance, incidents, provider outages, or product changes may affect access to the service.',
        ],
      },
      {
        id: 'account-suspension-or-termination',
        title: '10. Account Suspension or Termination',
        body: [
          'The Tavern may suspend or terminate access if an account is used unlawfully, abusively, or in a way that threatens the service, other users, or platform security.',
          'When appropriate, we will try to provide information about the reason and possible next steps.',
        ],
      },
      {
        id: 'account-deletion',
        title: '11. Account Deletion',
        body: [
          'Users may delete their account from Settings > Privacy & Data.',
          'The self-service deletion feature is available from the authenticated settings area. Users can also request deletion help by contacting contact@thetavern.fr.',
          'Deletion may remove or anonymize account data unless limited retention is required for legal, security, or operational reasons.',
        ],
      },
      {
        id: 'liability',
        title: '12. Liability',
        body: [
          'The Tavern is provided for organizing and coordinating board game activity. Users remain responsible for their real-world arrangements, interactions, and decisions.',
          'To the maximum extent permitted by law, The Tavern is not liable for indirect, incidental, or unforeseeable damage, or for user-generated content and third-party metadata.',
        ],
      },
      {
        id: 'changes-to-the-terms',
        title: '13. Changes to the Terms',
        body: [
          `Current terms version: ${CURRENT_TERMS_VERSION}. Last updated: ${LEGAL_UPDATED_AT_ISO}.`,
          'These Terms may be updated as The Tavern evolves, as new features are added, or as legal requirements change.',
          'When material changes require a new acceptance, The Tavern may ask users to review and accept the updated version before continuing to use the service.',
        ],
      },
      {
        id: 'contact',
        title: '14. Contact',
        body: [
          'For questions about these Terms of Service, contact contact@thetavern.fr.',
        ],
      },
    ],
  },
  '/legal': {
    eyebrow: 'Legal',
    title: 'Legal Notice',
    subtitle: 'Publisher, contact, hosting, intellectual property, and applicable law information.',
    meta: `Version: ${LEGAL_NOTICE.version} - Last updated: ${LEGAL_NOTICE.lastUpdated}`,
    footerMeta: `Version: ${LEGAL_NOTICE.version} - Last updated: ${LEGAL_NOTICE.lastUpdated}`,
    sections: [
      {
        id: 'publisher',
        title: '1. Publisher',
        body: [
          `Publisher name: ${LEGAL_NOTICE.publisherName}.`,
          `Operator: ${LEGAL_NOTICE.publisherOperator}.`,
          `Legal form: ${LEGAL_NOTICE.legalForm}.`,
          `SIREN: ${LEGAL_NOTICE.siren}.`,
          `VAT number: ${LEGAL_NOTICE.vatNumber}.`,
          `Registered address: ${LEGAL_NOTICE.registeredAddress}.`,
          `Publication director: ${LEGAL_NOTICE.publicationDirector}.`,
        ],
      },
      {
        id: 'contact',
        title: '2. Contact',
        body: [
          `For legal, privacy, or general requests, contact ${LEGAL_NOTICE.contactEmail}.`,
          'Additional contact details may be added here when the publisher information is completed.',
        ],
      },
      {
        id: 'hosting-provider',
        title: '3. Hosting Provider',
        body: [
          `Provider name: ${LEGAL_NOTICE.hostingProviderName}.`,
          LEGAL_NOTICE.hostingProviderWebsite
            ? `Provider website: ${LEGAL_NOTICE.hostingProviderWebsite}.`
            : 'Provider website: to be completed before public launch.',
          LEGAL_NOTICE.hostingProviderNote,
        ],
      },
      {
        id: 'intellectual-property',
        title: '4. Intellectual Property',
        body: [
          'All content, trademarks, logos, graphics, visual identity, and software associated with The Tavern are protected by applicable intellectual property laws and may not be reproduced or distributed without authorization.',
          'Board game names, covers, metadata, publishers, and designer information may belong to their respective rightsholders or data providers.',
          'Source code access, if any, does not grant rights to reuse The Tavern branding, original assets, or protected third-party content outside the permissions explicitly granted by the project owner.',
        ],
      },
      {
        id: 'liability',
        title: '5. Liability',
        body: [
          'The Tavern is provided on a best-effort basis as an evolving board game library and game night planning service.',
          'Access may be interrupted for maintenance, incidents, provider outages, security reasons, or product changes.',
          'Users are responsible for keeping their account credentials confidential and for the accuracy and legality of the information they add to the service.',
        ],
      },
      {
        id: 'applicable-law',
        title: '6. Applicable Law',
        body: [
          'These legal notices are governed by French law.',
          'Before The Tavern is opened publicly, the publisher and hosting provider details must be completed with the exact legally required information.',
        ],
      },
    ],
  },
  '/privacy/contact': {
    eyebrow: 'Privacy',
    title: 'Privacy Requests',
    subtitle:
      'Understand your personal data rights and how to submit a privacy request to The Tavern.',
    meta: `Updated ${LEGAL_UPDATED_AT}`,
    sections: [
      {
        id: 'your-rights',
        title: 'Your rights',
        body: [
          'Right of access: request a copy of the personal data we hold about you.',
          'Right to rectification: request correction of inaccurate or incomplete information.',
          'Right to erasure: request deletion of your personal data.',
          'Right to restriction: request temporary restriction of how your data is processed.',
          'Right to object: object to certain types of data processing.',
          'Right to portability: request a copy of your data in a machine-readable format.',
          'These rights may be subject to legal limits, identity verification, and safeguards needed to protect other users.',
        ],
      },
      {
        id: 'submit-request',
        title: 'How to submit a request',
        body: [
          'For any privacy-related request, contact us at contact@thetavern.fr.',
          'Please describe the request type and the account concerned. To protect your account, we may ask for information needed to verify your identity before processing a request.',
          'Do not send passwords, authentication tokens, or sensitive unrelated information by email.',
        ],
      },
      {
        id: 'request-types',
        title: 'Available request types',
        body: [
          'Access to personal data.',
          'Correction of profile information.',
          'Account deletion.',
          'Data export.',
          'Objection to data processing.',
          'Restriction of processing.',
          'Questions regarding privacy and personal data.',
        ],
      },
      {
        id: 'self-service',
        title: 'Self-service options',
        body: [
          'Data export: authenticated users can download a copy of their data from Settings > Privacy & Data.',
          'Account deletion: authenticated users can permanently delete their account from Settings > Privacy & Data.',
          'If you cannot access your account or need help, contact contact@thetavern.fr.',
        ],
      },
      {
        id: 'response-times',
        title: 'Response times',
        body: [
          'We aim to respond to privacy requests within one month, in accordance with applicable data protection regulations.',
          'If a request is complex or requires additional verification, we may need extra time and will explain the next steps.',
        ],
      },
      {
        id: 'future-workflows',
        title: 'Future request workflows',
        body: [
          'This page is the current entry point for privacy requests.',
          'Future versions may add dedicated request forms, request tracking, a status dashboard, or identity verification workflows.',
        ],
      },
    ],
  },
};

const legalPagesFr = {
  '/privacy': {
    eyebrow: 'Confidentialité',
    title: 'Politique de confidentialité',
    subtitle:
      'Comment The Tavern collecte, utilise, protège et vous laisse contrôler vos données personnelles.',
    meta: `Version : ${CURRENT_PRIVACY_VERSION} - Dernière mise à jour : ${LEGAL_UPDATED_AT_ISO}`,
    sections: [
      {
        id: 'responsable-traitement',
        title: '1. Responsable du traitement',
        body: [
          "The Tavern est responsable du traitement des données personnelles utilisées par l'application web disponible sur thetavern.fr.",
          "The Tavern est exploité par Sarto afin de fournir un service de ludothèque, de compagnons et d'organisation de soirées jeux.",
        ],
      },
      {
        id: 'contact',
        title: '2. Contact',
        body: [
          'Pour toute question de confidentialité ou demande RGPD, contactez contact@thetavern.fr.',
          "Vous pouvez utiliser cette adresse pour vos demandes d'accès, de rectification, de suppression, de portabilité, d'opposition ou de limitation.",
        ],
        links: [{ label: 'Gérer les demandes de confidentialité', to: '/privacy/contact' }],
      },
      {
        id: 'donnees-collectees',
        title: '3. Données collectées',
        body: [
          "Données de compte : nom d'utilisateur, adresse email, mot de passe haché, vérification email, jetons de réinitialisation et dates associées.",
          "Données de profil : nom affiché, avatar, titre d'aubergiste, devise, jeux favoris, tags, sceaux, statistiques et dates de mise à jour.",
          'Données sociales : relations de compagnons, invitations, présence en ligne, dernière activité et statuts associés.',
          'Données de ludothèque : jeux ajoutés, identifiants BoardGameGeek, métadonnées importées, traductions, notes, statut de possession et commentaires.',
          'Données de soirée : soirées organisées, compagnons invités, lieu, jeux candidats, votes, notifications et dates associées.',
          "Données locales : jeton d'authentification, langue, brouillons, état d'installation PWA, profil d'en-tête en cache et préférences de cookies.",
        ],
      },
      {
        id: 'finalites',
        title: '4. Finalités',
        body: [
          "Nous traitons ces données pour créer et sécuriser les comptes, authentifier les utilisateurs, gérer les ludothèques, inviter des compagnons, organiser des soirées, voter pour les jeux, envoyer des notifications et maintenir le service.",
          "Nous utilisons aussi des données techniques limitées pour la sécurité, la prévention des abus, le débogage, l'exploitation du service, les demandes d'assistance et les préférences email.",
          'Nous ne vendons pas vos données personnelles.',
        ],
      },
      {
        id: 'base-legale',
        title: '5. Base légale',
        body: [
          'Contrat : données nécessaires pour créer votre compte et fournir la ludothèque, les compagnons, les soirées, les votes et les notifications.',
          'Intérêt légitime : sécurité, prévention des abus, fiabilité, débogage et amélioration du produit sans porter atteinte à vos droits.',
          'Consentement : cookies non essentiels, emails marketing ou futures mesures optionnelles uniquement lorsque le consentement existe.',
          'Obligations légales : conservation ou traitement requis par la loi ou par une demande légale valide.',
        ],
      },
      {
        id: 'conservation',
        title: '6. Conservation',
        body: [
          'Les données sont conservées uniquement le temps nécessaire pour fournir le service, respecter les obligations légales, résoudre les litiges et faire appliquer les accords.',
          'La référence détaillée est le document docs/data-retention-policy.md du dépôt.',
          'À la suppression du compte, les données personnelles du compte et les enregistrements associés sont supprimés immédiatement sauf conservation technique ou légale strictement nécessaire.',
          'Les journaux techniques, métadonnées email transactionnelles et événements de sécurité peuvent être conservés jusqu’à 12 mois. Les notifications peuvent être conservées jusqu’à 24 mois.',
        ],
      },
      {
        id: 'fournisseurs',
        title: '7. Fournisseurs tiers',
        body: [
          "Hébergement : l'application de production fonctionne sur un serveur exploité pour The Tavern.",
          'Email : les emails transactionnels peuvent être envoyés via OVH SMTP avec noreply@thetavern.fr. Le code prend aussi en charge Resend si ce fournisseur est configuré plus tard.',
          'BoardGameGeek : lors de la recherche ou de l’import de jeux, The Tavern peut appeler les API BoardGameGeek et stocker les métadonnées importées.',
          "Cloudflare Turnstile : l'inscription publique peut utiliser Turnstile pour limiter les abus.",
          "Aucun fournisseur d'analyse, de marketing ou de publicité n'est actuellement documenté comme actif dans l'application.",
        ],
      },
      {
        id: 'cookies',
        title: '8. Cookies et stockage local',
        body: [
          "The Tavern utilise le stockage essentiel du navigateur pour l'authentification, la sécurité, la langue, les brouillons, l'installation PWA, le profil en cache et les préférences de cookies.",
          'Le stockage essentiel est toujours actif car il est nécessaire au fonctionnement du service.',
          'Les catégories optionnelles sont analyse, performance et marketing. Aucun fournisseur optionnel actif n’est actuellement documenté.',
        ],
        links: [{ label: 'Préférences de cookies', to: '/settings' }],
      },
      {
        id: 'droits',
        title: '9. Vos droits',
        body: [
          "Selon le RGPD, vous pouvez disposer d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité.",
          'Vous pouvez aussi retirer votre consentement lorsque le traitement repose sur celui-ci.',
          "Ces droits peuvent être soumis à des limites légales, à une vérification d'identité ou à des contraintes techniques nécessaires pour protéger les autres utilisateurs.",
        ],
      },
      {
        id: 'mise-a-jour',
        title: '10. Mises à jour',
        body: [
          `Version actuelle : ${CURRENT_PRIVACY_VERSION}. Dernière mise à jour : ${LEGAL_UPDATED_AT_ISO}.`,
          'Cette politique peut évoluer avec The Tavern, les fournisseurs utilisés ou les obligations légales.',
          'Lorsqu’un changement important nécessite une nouvelle acceptation, The Tavern peut demander aux utilisateurs de relire et accepter la version mise à jour.',
        ],
      },
    ],
  },
  '/terms': {
    eyebrow: 'Conditions',
    title: "Conditions d'utilisation",
    subtitle: "Les règles d'utilisation de The Tavern.",
    meta: `Version : ${CURRENT_TERMS_VERSION} - Dernière mise à jour : ${LEGAL_UPDATED_AT_ISO}`,
    sections: [
      {
        id: 'objet',
        title: '1. Objet du service',
        body: [
          'The Tavern aide les utilisateurs à créer et gérer une ludothèque, se connecter avec des compagnons, organiser des soirées jeux, envoyer des invitations, voter pour des jeux et recevoir des notifications associées.',
          'Le service est un produit en évolution. Les fonctionnalités peuvent changer, être améliorées ou retirées au fil du développement.',
        ],
      },
      {
        id: 'compte',
        title: '2. Création de compte',
        body: [
          "Pour utiliser le service, vous pouvez devoir créer un compte avec un nom d'utilisateur, une adresse email et un mot de passe.",
          "Vous devez fournir des informations exactes, garder vos identifiants confidentiels et nous prévenir si vous pensez que votre compte est compromis.",
          "Vous êtes responsable de l'activité effectuée via votre compte sauf si elle résulte d'un problème de sécurité causé par The Tavern.",
        ],
      },
      {
        id: 'responsabilites',
        title: '3. Responsabilités des utilisateurs',
        body: [
          'Vous acceptez d’utiliser The Tavern légalement et avec respect.',
          'Vous êtes responsable de l’exactitude et de la légalité des informations ajoutées, notamment profil, ludothèque, notes, détails de soirées et invitations.',
          'Vous devez respecter les autres utilisateurs et éviter tout comportement nuisible, trompeur, abusif ou perturbateur.',
        ],
      },
      {
        id: 'usage-accepte',
        title: '4. Usage acceptable',
        body: [
          'Vous ne devez pas utiliser The Tavern pour enfreindre la loi, usurper une identité, envoyer du spam, extraire des données privées, contourner les accès, perturber le service ou téléverser du contenu malveillant.',
          'Vous ne devez pas tenter d’accéder à des données ou comptes auxquels vous n’êtes pas autorisé.',
        ],
      },
      {
        id: 'contenu',
        title: '5. Contenu utilisateur',
        body: [
          'Vous conservez la responsabilité du contenu fourni à The Tavern, notamment textes de profil, avatars, notes, invitations et informations de soirées.',
          'En ajoutant du contenu, vous autorisez The Tavern à le traiter, l’afficher, le stocker et le transmettre lorsque cela est nécessaire au service.',
          'N’ajoutez pas de contenu illégal, contrefaisant, abusif ou dangereux.',
        ],
      },
      {
        id: 'soirees',
        title: '6. Soirées et invitations',
        body: [
          'The Tavern permet d’organiser des soirées jeux, inviter des compagnons, voter pour des jeux et coordonner qui apporte quoi.',
          'Vous êtes responsable des invitations, lieux, dates et détails partagés avec les autres utilisateurs.',
        ],
      },
      {
        id: 'notifications',
        title: '7. Notifications et emails',
        body: [
          'The Tavern peut envoyer des emails transactionnels et notifications internes pour la vérification de compte, la réinitialisation de mot de passe, les invitations, les changements de soirée et la sécurité.',
          'Les emails marketing, s’ils sont introduits plus tard, nécessiteront un consentement adapté et un mécanisme de désinscription.',
        ],
      },
      {
        id: 'disponibilite',
        title: '8. Disponibilité',
        body: [
          'The Tavern est fourni comme service en évolution, sans garantie de disponibilité continue ou d’absence d’erreur.',
          'Maintenance, incidents, pannes de fournisseurs ou changements produit peuvent affecter l’accès au service.',
        ],
      },
      {
        id: 'suppression',
        title: '9. Suppression du compte',
        body: [
          'Les utilisateurs peuvent supprimer leur compte depuis Réglages > Confidentialité et données.',
          'Une aide à la suppression peut aussi être demandée via contact@thetavern.fr.',
          'La suppression peut retirer ou anonymiser les données du compte sauf conservation limitée requise pour des raisons légales, de sécurité ou d’exploitation.',
        ],
      },
      {
        id: 'contact',
        title: '10. Contact',
        body: [
          "Pour toute question concernant ces conditions d'utilisation, contactez contact@thetavern.fr.",
        ],
      },
    ],
  },
  '/legal': {
    eyebrow: 'Mentions légales',
    title: 'Mentions légales',
    subtitle: "Informations sur l'éditeur, le contact, l'hébergement, la propriété intellectuelle et le droit applicable.",
    meta: `Version : ${LEGAL_NOTICE.version} - Dernière mise à jour : ${LEGAL_NOTICE.lastUpdated}`,
    footerMeta: `Version : ${LEGAL_NOTICE.version} - Dernière mise à jour : ${LEGAL_NOTICE.lastUpdated}`,
    sections: [
      {
        id: 'editeur',
        title: '1. Éditeur',
        body: [
          `Nom de l'éditeur : ${LEGAL_NOTICE.publisherName}.`,
          `Opérateur : ${LEGAL_NOTICE.publisherOperator}.`,
          `Forme juridique : ${LEGAL_TODO_FR}.`,
          `SIREN : ${LEGAL_TODO_FR}.`,
          `Numéro de TVA : ${LEGAL_TODO_FR}.`,
          `Adresse enregistrée : ${LEGAL_TODO_FR}.`,
          `Directeur de la publication : ${LEGAL_TODO_FR}.`,
        ],
      },
      {
        id: 'contact',
        title: '2. Contact',
        body: [
          `Pour toute demande légale, de confidentialité ou générale, contactez ${LEGAL_NOTICE.contactEmail}.`,
          "Des informations de contact complémentaires pourront être ajoutées lorsque les informations d'éditeur seront complètes.",
        ],
      },
      {
        id: 'hebergeur',
        title: '3. Hébergeur',
        body: [
          'Nom du fournisseur : serveur auto-géré / VPS.',
          LEGAL_NOTICE.hostingProviderWebsite
            ? `Site du fournisseur : ${LEGAL_NOTICE.hostingProviderWebsite}.`
            : 'Site du fournisseur : à compléter avant l’ouverture publique.',
          'Le dépôt documente un déploiement Docker Compose sur ubuntu@thetavern.fr dans /opt/thetavern. La société d’hébergement exacte devra être complétée avant l’ouverture publique.',
        ],
      },
      {
        id: 'propriete',
        title: '4. Propriété intellectuelle',
        body: [
          'Les contenus, marques, logos, graphismes, identité visuelle et logiciels associés à The Tavern sont protégés par les lois applicables et ne peuvent pas être reproduits sans autorisation.',
          'Les noms, couvertures, métadonnées, éditeurs et auteurs de jeux peuvent appartenir à leurs ayants droit ou fournisseurs de données.',
        ],
      },
      {
        id: 'droit',
        title: '5. Droit applicable',
        body: [
          'Ces mentions légales sont régies par le droit français.',
          'Avant l’ouverture publique de The Tavern, les informations exactes requises sur l’éditeur et l’hébergeur devront être complétées.',
        ],
      },
    ],
  },
  '/privacy/contact': {
    eyebrow: 'Confidentialité',
    title: 'Demandes de confidentialité',
    subtitle:
      'Comprendre vos droits sur les données personnelles et savoir comment envoyer une demande à The Tavern.',
    meta: `Mise à jour : ${LEGAL_UPDATED_AT}`,
    sections: [
      {
        id: 'droits',
        title: 'Vos droits',
        body: [
          "Droit d'accès : demander une copie des données personnelles que nous détenons à votre sujet.",
          'Droit de rectification : demander la correction d’informations inexactes ou incomplètes.',
          'Droit à l’effacement : demander la suppression de vos données personnelles.',
          'Droit à la limitation : demander une restriction temporaire du traitement.',
          'Droit d’opposition : vous opposer à certains traitements.',
          'Droit à la portabilité : demander une copie de vos données dans un format lisible par machine.',
          'Ces droits peuvent être soumis à des limites légales, une vérification d’identité et des protections nécessaires pour les autres utilisateurs.',
        ],
      },
      {
        id: 'envoyer',
        title: 'Envoyer une demande',
        body: [
          'Pour toute demande liée à la confidentialité, contactez contact@thetavern.fr.',
          'Décrivez le type de demande et le compte concerné. Pour protéger votre compte, nous pouvons demander des informations permettant de vérifier votre identité.',
          'N’envoyez pas de mots de passe, jetons d’authentification ou informations sensibles sans rapport par email.',
        ],
      },
      {
        id: 'options',
        title: 'Options en libre-service',
        body: [
          'Export de données : les utilisateurs connectés peuvent télécharger une copie de leurs données depuis Réglages > Confidentialité et données.',
          'Suppression du compte : les utilisateurs connectés peuvent supprimer définitivement leur compte depuis Réglages > Confidentialité et données.',
          'Si vous ne pouvez pas accéder à votre compte ou si vous avez besoin d’aide, contactez contact@thetavern.fr.',
        ],
      },
      {
        id: 'delais',
        title: 'Délais de réponse',
        body: [
          'Nous visons une réponse aux demandes de confidentialité sous un mois, conformément à la réglementation applicable.',
          'Si une demande est complexe ou nécessite une vérification supplémentaire, nous pouvons avoir besoin de plus de temps et expliquerons les prochaines étapes.',
        ],
      },
    ],
  },
};

const renderTextWithLinks = (text) => {
  const parts = text.split(/(contact@thetavern\.fr)/g);

  return parts.map((part, index) =>
    part === 'contact@thetavern.fr' ? (
      <Link key={`${part}-${index}`} href="mailto:contact@thetavern.fr">
        {part}
      </Link>
    ) : (
      part
    )
  );
};

const LegalPage = () => {
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const localizedPages = language?.startsWith('fr') ? legalPagesFr : legalPages;
  const page = localizedPages[location.pathname] || localizedPages['/privacy'];

  useEffect(() => {
    document.title = `${page.title} | The Tavern`;
  }, [page.title]);

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        py: { xs: 3, md: 5 },
        background:
          'radial-gradient(circle at 18% 8%, rgba(215,160,75,0.2), transparent 24rem), radial-gradient(circle at 86% 82%, rgba(15,76,69,0.16), transparent 28rem)',
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Box component={RouterLink} to="/" sx={{ color: 'inherit', textDecoration: 'none' }}>
              <BrandLogo emblemSize={52} />
            </Box>
            <Button component={RouterLink} to="/login" variant="outlined">
              {t('authForgot.backToLogin')}
            </Button>
          </Stack>

          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
                    {page.eyebrow}
                  </Typography>
                  <Typography component="h1" variant="h1">{page.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 680 }}>
                    {page.subtitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {page.meta}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button component={RouterLink} to="/privacy" size="small" variant="outlined">
                    {localizedPages['/privacy'].title}
                  </Button>
                  <Button component={RouterLink} to="/terms" size="small" variant="outlined">
                    {localizedPages['/terms'].title}
                  </Button>
                  <Button component={RouterLink} to="/legal" size="small" variant="outlined">
                    {localizedPages['/legal'].title}
                  </Button>
                  <Button component={RouterLink} to="/privacy/contact" size="small" variant="outlined">
                    {localizedPages['/privacy/contact'].title}
                  </Button>
                </Stack>

                {page.sections.length > 1 ? (
                  <Box component="nav" aria-label={`${page.title} sections`}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {page.sections.map((section) => (
                        <Button
                          key={section.title}
                          component="a"
                          href={`#${section.id || section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          size="small"
                          variant="text"
                        >
                          {section.title}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                ) : null}

                <Divider />

                <Stack spacing={3}>
                  {page.sections.map((section) => (
                    <Box
                      id={section.id || section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
                      key={section.title}
                      component="section"
                      sx={{ scrollMarginTop: 24 }}
                    >
                      <Typography component="h2" variant="h3" sx={{ mb: 1 }}>
                        {section.title}
                      </Typography>
                      <Stack spacing={1}>
                        {section.body.map((paragraph) => (
                          <Typography key={paragraph} color="text.secondary">
                            {renderTextWithLinks(paragraph)}
                          </Typography>
                        ))}
                        {section.links?.length ? (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                            {section.links.map((link) => (
                              <Button
                                key={link.to}
                                component={RouterLink}
                                to={link.to}
                                size="small"
                                variant="outlined"
                              >
                                {link.label}
                              </Button>
                            ))}
                          </Stack>
                        ) : null}
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                <Divider />

                <Typography variant="body2" color="text.secondary">
                  Contact: <Link href="mailto:contact@thetavern.fr">contact@thetavern.fr</Link>
                </Typography>
                {page.footerMeta ? (
                  <Typography variant="body2" color="text.secondary">
                    {page.footerMeta}
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default LegalPage;
